import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Banknote, RefreshCw, Loader2, Lock, Download, Ban, FileSpreadsheet, FileText,
  ChevronRight, CalendarRange, AlertTriangle, Shield,
} from 'lucide-react';
import API_BASE from '../../utils/apiBase';
import HelpSectionHint from '../../components/portal/HelpSectionHint';

const API = API_BASE;

const STATUS_STYLES = {
  draft: 'bg-slate-50 text-slate-700 border-slate-200',
  locked: 'bg-amber-50 text-amber-800 border-amber-200',
  exported: 'bg-green-50 text-green-700 border-green-200',
  voided: 'bg-red-50 text-red-700 border-red-200',
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const toLocalInputValue = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fromLocalInputValue = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

const formatMoney = (amount, currency = 'GBP') => {
  if (amount == null || Number.isNaN(Number(amount))) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(Number(amount));
};

export default function PayrollPage() {
  const { token, isAdmin } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [eligible, setEligible] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [generateNotes, setGenerateNotes] = useState('');
  const [payrollProfiles, setPayrollProfiles] = useState([]);
  const [payrollProfileSummary, setPayrollProfileSummary] = useState(null);
  const [selectedPayrollEmployeeId, setSelectedPayrollEmployeeId] = useState('');
  const [payrollProfileDraft, setPayrollProfileDraft] = useState(null);
  const [payrollProfileSaving, setPayrollProfileSaving] = useState(false);
  const [paymentDate, setPaymentDate] = useState('');
  const [calculationPreview, setCalculationPreview] = useState(null);
  const [calculationLoading, setCalculationLoading] = useState(false);

  const fetchBatches = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      const url = `${API}/payroll-batches${params.toString() ? `?${params}` : ''}`;
      const res = await axios.get(url, { headers });
      setBatches(res.data?.batches || []);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Payroll is admin-only');
      } else {
        toast.error('Failed to load payroll batches');
      }
    } finally {
      setLoading(false);
    }
  }, [headers, statusFilter]);

  const fetchPayrollProfiles = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/payroll-profiles`, { headers });
      const profiles = res.data?.profiles || [];
      setPayrollProfiles(profiles);
      setPayrollProfileSummary(res.data?.summary || null);
      setSelectedPayrollEmployeeId((current) => {
        if (current && profiles.some((row) => row.employee_id === current)) return current;
        const firstIncomplete = profiles.find((row) => !row.readiness?.ready);
        return firstIncomplete?.employee_id || profiles[0]?.employee_id || '';
      });
    } catch (err) {
      if (err.response?.status !== 403) toast.error('Failed to load statutory payroll setup');
    }
  }, [headers]);

  const fetchEligible = useCallback(async () => {
    const start = fromLocalInputValue(periodStart);
    const end = fromLocalInputValue(periodEnd);
    if (!start || !end) {
      setEligible(null);
      return;
    }
    try {
      const params = new URLSearchParams({ pay_period_start: start, pay_period_end: end });
      const res = await axios.get(`${API}/payroll-batches/eligible-timesheets?${params}`, { headers });
      setEligible(res.data);
    } catch {
      setEligible(null);
    }
  }, [headers, periodStart, periodEnd]);

  const applyPeriodPreset = useCallback(async (preset) => {
    try {
      const res = await axios.get(`${API}/timesheets/suggested-period?preset=${preset}`, { headers });
      const start = res.data?.pay_period_start;
      const end = res.data?.pay_period_end;
      if (start) setPeriodStart(toLocalInputValue(start));
      if (end) setPeriodEnd(toLocalInputValue(end));
      return res.data;
    } catch {
      toast.error('Could not load pay period');
      return null;
    }
  }, [headers]);

  useEffect(() => {
    if (!isAdmin()) return;
    fetchBatches();
    fetchPayrollProfiles();
    applyPeriodPreset('last_week');
  }, [fetchBatches, fetchPayrollProfiles, applyPeriodPreset, isAdmin]);

  useEffect(() => {
    if (!isAdmin()) return;
    fetchEligible();
  }, [fetchEligible, isAdmin]);

  const fetchDetail = useCallback(async (id) => {
    if (!id) return;
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/payroll-batches/${id}`, { headers });
      setDetail(res.data?.batch || null);
    } catch {
      toast.error('Failed to load payroll batch');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    setCalculationPreview(null);
    setPaymentDate('');
    if (selectedId) fetchDetail(selectedId);
    else setDetail(null);
  }, [selectedId, fetchDetail]);

  const formatApiError = (err, fallback) => {
    const data = err?.response?.data;
    const detail = data?.detail ?? data?.message;
    if (typeof detail === 'string') return detail;
    if (detail?.message) {
      const missing = detail.missing_employees || [];
      if (missing.length) {
        return `${detail.message}: ${missing.join(', ')}`;
      }
      return detail.message;
    }
    return fallback;
  };

  const selectedPayrollProfile = useMemo(
    () => payrollProfiles.find((row) => row.employee_id === selectedPayrollEmployeeId) || null,
    [payrollProfiles, selectedPayrollEmployeeId],
  );

  useEffect(() => {
    if (!selectedPayrollProfile) {
      setPayrollProfileDraft(null);
      return;
    }
    setPayrollProfileDraft({
      tax_code: selectedPayrollProfile.tax_code || '',
      tax_basis: selectedPayrollProfile.tax_basis || 'cumulative',
      ni_category: selectedPayrollProfile.ni_category || '',
      starter_source: selectedPayrollProfile.starter_source || '',
      starter_declaration: selectedPayrollProfile.starter_declaration || '',
      opening_pay_to_date: selectedPayrollProfile.opening_pay_to_date ?? '',
      opening_tax_to_date: selectedPayrollProfile.opening_tax_to_date ?? '',
      student_loan_plan: selectedPayrollProfile.student_loan_plan || 'none',
      postgraduate_loan: Boolean(selectedPayrollProfile.postgraduate_loan),
      pension_status: selectedPayrollProfile.pension_status || 'not_assessed',
      pension_scheme_name: selectedPayrollProfile.pension_scheme_name || '',
      pension_employee_rate: selectedPayrollProfile.pension_employee_rate ?? '',
      pension_employer_rate: selectedPayrollProfile.pension_employer_rate ?? '',
      pension_basis: selectedPayrollProfile.pension_basis || '',
      pension_tax_relief_method: selectedPayrollProfile.pension_tax_relief_method || '',
      payroll_notes: selectedPayrollProfile.payroll_notes || '',
    });
  }, [selectedPayrollProfile]);

  const savePayrollProfile = async () => {
    if (!selectedPayrollEmployeeId || !payrollProfileDraft) return;
    setPayrollProfileSaving(true);
    try {
      const payload = {
        ...payrollProfileDraft,
        pension_employee_rate: payrollProfileDraft.pension_employee_rate === '' ? null : Number(payrollProfileDraft.pension_employee_rate),
        pension_employer_rate: payrollProfileDraft.pension_employer_rate === '' ? null : Number(payrollProfileDraft.pension_employer_rate),
        opening_pay_to_date: payrollProfileDraft.opening_pay_to_date === '' ? null : Number(payrollProfileDraft.opening_pay_to_date),
        opening_tax_to_date: payrollProfileDraft.opening_tax_to_date === '' ? null : Number(payrollProfileDraft.opening_tax_to_date),
      };
      await axios.put(`${API}/payroll-profiles/${selectedPayrollEmployeeId}`, payload, { headers });
      toast.success('Statutory payroll setup saved');
      await fetchPayrollProfiles();
    } catch (err) {
      toast.error(formatApiError(err, 'Could not save statutory payroll setup'));
    } finally {
      setPayrollProfileSaving(false);
    }
  };

  const handleGenerate = async () => {
    const start = fromLocalInputValue(periodStart);
    const end = fromLocalInputValue(periodEnd);
    if (!start || !end) {
      toast.error('Set pay period start and end');
      return;
    }
    if (eligible?.blocked_count > 0) {
      toast.error('Some workers are missing pay rates — set rates before generating');
      return;
    }
    if (!eligible?.ready_count) {
      toast.error('No locked/exported timesheets ready for payroll in this period');
      return;
    }
    if (!window.confirm(
      `Create payroll batch for ${eligible.ready_count} worker(s)? Estimated total uses current pay rates.`,
    )) return;

    setActionLoading(true);
    try {
      const res = await axios.post(
        `${API}/payroll-batches/generate`,
        { pay_period_start: start, pay_period_end: end, notes: generateNotes || undefined },
        { headers },
      );
      const batch = res.data?.batch;
      toast.success(`Payroll batch ${batch?.reference || ''} created`);
      await fetchBatches();
      await fetchEligible();
      if (batch?.id) setSelectedId(batch.id);
    } catch (err) {
      toast.error(formatApiError(err, 'Generate failed'));
    } finally {
      setActionLoading(false);
    }
  };

  const runAction = async (suffix, successMsg) => {
    if (!selectedId) return;
    setActionLoading(true);
    try {
      await axios.post(`${API}/payroll-batches/${selectedId}${suffix}`, {}, { headers });
      toast.success(successMsg);
      await fetchBatches();
      await fetchDetail(selectedId);
      await fetchEligible();
    } catch (err) {
      toast.error(formatApiError(err, 'Action failed'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleLock = () => {
    if (!window.confirm('Lock this payroll batch? It cannot be edited after lock.')) return;
    runAction('/lock', 'Payroll batch locked');
  };

  const handleVoid = () => {
    if (!window.confirm('Void this batch and release timesheets back to payroll-ready?')) return;
    runAction('/void', 'Payroll batch voided');
  };

  const handleCalculationPreview = async () => {
    if (!selectedId) return;
    if (!paymentDate) {
      toast.error('Set the payment date used for PAYE');
      return;
    }
    setCalculationLoading(true);
    try {
      const res = await axios.post(
        `${API}/payroll-batches/${selectedId}/calculation-preview`,
        { payment_date: paymentDate },
        { headers },
      );
      setCalculationPreview(res.data || null);
      const blocked = Number(res.data?.summary?.blocked_count || 0);
      if (blocked > 0) {
        toast.warning(`Preview calculated with ${blocked} worker(s) blocked by incomplete payroll setup`);
      } else {
        toast.success('Tax and net pay preview calculated');
      }
    } catch (err) {
      setCalculationPreview(null);
      toast.error(formatApiError(err, 'Could not calculate tax and net pay preview'));
    } finally {
      setCalculationLoading(false);
    }
  };

  const handleExport = async (format = 'csv') => {
    if (!selectedId) return;
    setActionLoading(true);
    try {
      const res = await axios.post(
        `${API}/payroll-batches/${selectedId}/export`,
        { format },
        { headers, responseType: 'blob' },
      );
      const ref = detail?.reference || selectedId;
      const isPdf = format === 'pdf';
      const blob = new Blob([res.data], { type: isPdf ? 'application/pdf' : 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${ref}.${isPdf ? 'pdf' : 'csv'}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(isPdf ? 'Payroll PDF downloaded' : 'Payroll CSV downloaded');
      await fetchBatches();
      await fetchDetail(selectedId);
    } catch (err) {
      toast.error(formatApiError(err, 'Export failed'));
    } finally {
      setActionLoading(false);
    }
  };

  if (!isAdmin()) {
    return (
      <div className="p-8 max-w-lg mx-auto text-center space-y-4">
        <Shield className="h-12 w-12 text-text-muted mx-auto" />
        <h1 className="text-xl font-heading font-semibold">Payroll — admin only</h1>
        <p className="text-sm text-text-muted">
          Payroll contains salary data. Only admin accounts can access this area.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-heading font-semibold text-text-primary flex items-center gap-2">
            <Banknote className="h-7 w-7 text-primary" />
            Payroll
          </h1>
          <p className="text-sm text-text-muted mt-1">
            Gross pay from locked timesheets (admin only) — worker side of the predictable financial pipeline. Shifts (approved) → Timesheets (lock) → Payroll + parallel client Invoices → Financially Closed (see Shifts archive).
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { fetchBatches(); fetchPayrollProfiles(); }} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Shield className="h-5 w-5" />
            HMRC & statutory payroll setup
          </CardTitle>
          <p className="text-sm text-text-muted">
            Complete starter details before gross-to-net payroll. CareOps does not guess tax codes or National Insurance categories.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2 text-sm">
            <Badge variant="outline">Tax year {payrollProfileSummary?.tax_year || '—'}</Badge>
            <Badge className="bg-green-50 text-green-700 border-green-200">Ready {payrollProfileSummary?.ready ?? 0}</Badge>
            <Badge className="bg-amber-50 text-amber-800 border-amber-200">Incomplete {payrollProfileSummary?.incomplete ?? 0}</Badge>
          </div>

          {payrollProfiles.length === 0 ? (
            <p className="text-sm text-text-muted">No onboarding or active staff are available for payroll setup yet.</p>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="payroll-profile-worker">Worker</Label>
                  <select
                    id="payroll-profile-worker"
                    className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                    value={selectedPayrollEmployeeId}
                    onChange={(e) => setSelectedPayrollEmployeeId(e.target.value)}
                  >
                    {payrollProfiles.map((row) => (
                      <option key={row.employee_id} value={row.employee_id}>
                        {row.employee_name || row.employee_code || row.employee_id} {row.readiness?.ready ? '✓' : '— setup needed'}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end">
                  {selectedPayrollProfile?.readiness?.ready ? (
                    <Badge className="bg-green-50 text-green-700 border-green-200">Payroll setup complete</Badge>
                  ) : (
                    <div className="text-xs text-amber-800">
                      Missing: {(selectedPayrollProfile?.readiness?.missing_fields || []).map((item) => item.label).join(', ') || 'statutory setup'}
                    </div>
                  )}
                </div>
              </div>

              {payrollProfileDraft ? (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <div>
                    <Label>PAYE tax code</Label>
                    <Input value={payrollProfileDraft.tax_code} onChange={(e) => setPayrollProfileDraft((d) => ({ ...d, tax_code: e.target.value }))} placeholder="e.g. 1257L" />
                  </div>
                  <div>
                    <Label>Tax basis</Label>
                    <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={payrollProfileDraft.tax_basis} onChange={(e) => setPayrollProfileDraft((d) => ({ ...d, tax_basis: e.target.value }))}>
                      <option value="cumulative">Cumulative</option>
                      <option value="week1_month1">Week 1 / Month 1</option>
                    </select>
                  </div>
                  <div>
                    <Label>NI category</Label>
                    <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={payrollProfileDraft.ni_category} onChange={(e) => setPayrollProfileDraft((d) => ({ ...d, ni_category: e.target.value }))}>
                      <option value="">Select</option>
                      {['A','B','C','D','E','F','H','I','J','K','L','M','N','S','V','Z'].map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>Starter source</Label>
                    <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={payrollProfileDraft.starter_source} onChange={(e) => setPayrollProfileDraft((d) => ({ ...d, starter_source: e.target.value }))}>
                      <option value="">Select</option>
                      <option value="p45">P45</option>
                      <option value="starter_checklist">Starter Checklist</option>
                      <option value="hmrc_notice">HMRC coding notice</option>
                    </select>
                  </div>
                  {payrollProfileDraft.starter_source === 'starter_checklist' ? (
                    <div>
                      <Label>Starter declaration</Label>
                      <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={payrollProfileDraft.starter_declaration} onChange={(e) => setPayrollProfileDraft((d) => ({ ...d, starter_declaration: e.target.value }))}>
                        <option value="">Select</option><option value="A">A</option><option value="B">B</option><option value="C">C</option>
                      </select>
                    </div>
                  ) : null}
                  {payrollProfileDraft.starter_source === 'p45' ? (
                    <>
                      <div>
                        <Label>P45 previous pay to date</Label>
                        <Input type="number" min="0" step="0.01" value={payrollProfileDraft.opening_pay_to_date} onChange={(e) => setPayrollProfileDraft((d) => ({ ...d, opening_pay_to_date: e.target.value }))} placeholder="0.00" />
                      </div>
                      <div>
                        <Label>P45 previous tax to date</Label>
                        <Input type="number" step="0.01" value={payrollProfileDraft.opening_tax_to_date} onChange={(e) => setPayrollProfileDraft((d) => ({ ...d, opening_tax_to_date: e.target.value }))} placeholder="0.00" />
                      </div>
                    </>
                  ) : null}
                  <div>
                    <Label>Student loan</Label>
                    <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={payrollProfileDraft.student_loan_plan} onChange={(e) => setPayrollProfileDraft((d) => ({ ...d, student_loan_plan: e.target.value }))}>
                      <option value="none">None</option><option value="plan1">Plan 1</option><option value="plan2">Plan 2</option><option value="plan4">Plan 4</option><option value="plan5">Plan 5</option>
                    </select>
                  </div>
                  <div>
                    <Label>Pension status</Label>
                    <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={payrollProfileDraft.pension_status} onChange={(e) => setPayrollProfileDraft((d) => ({ ...d, pension_status: e.target.value }))}>
                      <option value="not_assessed">Not assessed</option><option value="eligible_jobholder">Eligible jobholder</option><option value="non_eligible_jobholder">Non-eligible jobholder</option><option value="entitled_worker">Entitled worker</option><option value="enrolled">Enrolled</option><option value="opted_out">Opted out</option><option value="postponed">Postponed</option><option value="not_applicable">Not applicable</option>
                    </select>
                  </div>
                  {payrollProfileDraft.pension_status === 'enrolled' ? (
                    <>
                      <div>
                        <Label>Pension basis</Label>
                        <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={payrollProfileDraft.pension_basis} onChange={(e) => setPayrollProfileDraft((d) => ({ ...d, pension_basis: e.target.value }))}>
                          <option value="">Select</option>
                          <option value="qualifying_earnings">Qualifying earnings</option>
                          <option value="full_pay">Full pay</option>
                        </select>
                      </div>
                      <div>
                        <Label>Employee payroll deduction %</Label>
                        <Input type="number" min="0" max="100" step="0.01" value={payrollProfileDraft.pension_employee_rate} onChange={(e) => setPayrollProfileDraft((d) => ({ ...d, pension_employee_rate: e.target.value }))} />
                      </div>
                      <div>
                        <Label>Employer pension %</Label>
                        <Input type="number" min="0" max="100" step="0.01" value={payrollProfileDraft.pension_employer_rate} onChange={(e) => setPayrollProfileDraft((d) => ({ ...d, pension_employer_rate: e.target.value }))} />
                      </div>
                      <div>
                        <Label>Pension tax relief</Label>
                        <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={payrollProfileDraft.pension_tax_relief_method} onChange={(e) => setPayrollProfileDraft((d) => ({ ...d, pension_tax_relief_method: e.target.value }))}>
                          <option value="">Select</option>
                          <option value="net_pay_arrangement">Net pay arrangement — reduces PAYE taxable pay</option>
                          <option value="relief_at_source">Relief at source — PAYE taxable pay unchanged</option>
                        </select>
                      </div>
                      <div>
                        <Label>Pension scheme (optional)</Label>
                        <Input value={payrollProfileDraft.pension_scheme_name} onChange={(e) => setPayrollProfileDraft((d) => ({ ...d, pension_scheme_name: e.target.value }))} placeholder="Scheme/provider name" />
                      </div>
                    </>
                  ) : null}
                  <label className="flex items-center gap-2 pt-7 text-sm">
                    <input type="checkbox" checked={payrollProfileDraft.postgraduate_loan} onChange={(e) => setPayrollProfileDraft((d) => ({ ...d, postgraduate_loan: e.target.checked }))} />
                    Postgraduate loan
                  </label>
                </div>
              ) : null}

              <div className="flex justify-end">
                <Button onClick={savePayrollProfile} disabled={!payrollProfileDraft || payrollProfileSaving}>
                  {payrollProfileSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Save statutory setup
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card data-help-section="eligible">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <CalendarRange className="h-5 w-5" />
            Pay period
            <HelpSectionHint sectionId="eligible" />
          </CardTitle>
          <p className="text-sm text-text-muted">
            Includes locked or exported timesheets not yet on a payroll batch. This is the next step after Timesheets lock in the predictable pipeline.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <Label htmlFor="payroll-period-start">Period start</Label>
              <Input
                id="payroll-period-start"
                type="datetime-local"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="payroll-period-end">Period end</Label>
              <Input
                id="payroll-period-end"
                type="datetime-local"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
              />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="payroll-notes">Notes (optional)</Label>
              <Input
                id="payroll-notes"
                value={generateNotes}
                onChange={(e) => setGenerateNotes(e.target.value)}
                placeholder="e.g. June week 1 pay run"
              />
            </div>
          </div>

          {eligible ? (
            <div className="text-sm text-text-muted space-y-2">
              <p>
                Ready: <strong>{eligible.ready_count}</strong> timesheet(s) with pay rates.
              </p>
              {eligible.blocked_count > 0 ? (
                <p className="text-amber-800 text-xs flex items-start gap-1">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>
                    {eligible.blocked_count} worker(s) missing pay rates:{' '}
                    {(eligible.missing_pay_rates || []).map((m) => m.employee_name).join(', ')}.
                    Set a worker-specific rate on the employee record or complete the HCA/Nurse defaults in Settings.
                  </span>
                </p>
              ) : null}
            </div>
          ) : null}

          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => applyPeriodPreset('last_week')}>
              Last week
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => applyPeriodPreset('current_week')}>
              Current week
            </Button>
            <Button
              onClick={handleGenerate}
              disabled={actionLoading || !eligible?.ready_count || eligible?.blocked_count > 0}
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />}
              Generate payroll batch
            </Button>
          </div>
          <p className="text-[11px] text-text-muted pt-2">Worker-specific overrides plus HCA/Nurse tenant defaults keep payroll predictable. Client charge rates stay in Organisations.</p>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card data-help-section="batch" data-tour="payroll-batches">
          <CardHeader className="flex flex-row items-center justify-between gap-2">
            <CardTitle className="text-lg flex items-center gap-2">
              Payroll batches
              <HelpSectionHint sectionId="batch" />
            </CardTitle>
            <select
              className="text-sm border rounded-md px-2 py-1"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All statuses</option>
              <option value="draft">Draft</option>
              <option value="locked">Locked</option>
              <option value="exported">Exported</option>
              <option value="voided">Voided</option>
            </select>
          </CardHeader>
          <CardContent className="space-y-2 max-h-[28rem] overflow-y-auto">
            {loading && <p className="text-sm text-text-muted">Loading…</p>}
            {!loading && batches.length === 0 && (
              <p className="text-sm text-text-muted">No payroll batches yet. Approve shifts under Shifts, generate+lock a timesheet, then return here.</p>
            )}
            {batches.map((batch) => (
              <button
                key={batch.id}
                type="button"
                onClick={() => setSelectedId(batch.id)}
                className={`w-full text-left rounded-xl border p-3 transition-colors ${
                  selectedId === batch.id ? 'border-primary bg-accent/40' : 'border-[#E4E8EB] hover:bg-[#F8FAFA]'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-sm">{batch.reference}</span>
                  <div className="flex items-center gap-1">
                    <Badge className={STATUS_STYLES[batch.status] || ''}>{batch.status}</Badge>
                    {(batch.status === 'locked' || batch.status === 'exported') && (
                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">Advances to Financially Closed (when invoices complete)</Badge>
                    )}
                  </div>
                </div>
                <p className="text-xs text-text-muted mt-1">
                  {batch.worker_count} worker(s) · {formatMoney(batch.total_gross_pay)}
                </p>
              </button>
            ))}
          </CardContent>
        </Card>

        <Card data-tour="payroll-detail" data-help-section="batch">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              Batch detail
              <HelpSectionHint sectionId="batch" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!selectedId && (
              <p className="text-sm text-text-muted">Select a batch to review worker lines.</p>
            )}
            {selectedId && detailLoading && (
              <div className="flex items-center gap-2 text-sm text-text-muted">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            )}
            {detail && !detailLoading && (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-heading font-semibold">{detail.reference}</span>
                  <Badge className={STATUS_STYLES[detail.status] || ''}>{detail.status}</Badge>
                </div>
                <p className="text-sm text-text-muted">
                  {formatDateTime(detail.pay_period_start)} → {formatDateTime(detail.pay_period_end)}
                </p>
                <p className="text-lg font-semibold text-text-primary">
                  Total gross: {formatMoney(detail.total_gross_pay)}
                </p>
                <p className="text-xs text-text-muted -mt-1">
                  Locked payroll batches + matching client invoices (paid/sent) mark the underlying attendance records as Financially Closed in the Shifts archive. This is the end of the predictable pipeline.
                </p>

                <div className="rounded-xl border border-[#E4E8EB] overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F8FAFA] text-left text-xs text-text-muted">
                      <tr>
                        <th className="px-3 py-2">Worker</th>
                        <th className="px-3 py-2">Hours</th>
                        <th className="px-3 py-2">Rate</th>
                        <th className="px-3 py-2">Gross</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detail.items || []).map((item) => (
                        <tr key={item.id} className="border-t border-[#E4E8EB]">
                          <td className="px-3 py-2">
                            <div>{item.employee_name}</div>
                            <div className="text-xs text-text-muted">{item.timesheet_reference}</div>
                          </td>
                          <td className="px-3 py-2">{Number(item.total_hours || 0).toFixed(2)}</td>
                          <td className="px-3 py-2">
                            {formatMoney(item.rate_amount)}/hr
                          </td>
                          <td className="px-3 py-2 font-medium">{formatMoney(item.gross_pay)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4 space-y-4">
                  <div className="flex flex-col gap-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-medium text-text-primary">Tax & net pay preview</h3>
                      <Badge className="bg-amber-100 text-amber-900 border-amber-300">Shadow — not final</Badge>
                    </div>
                    <p className="text-xs text-text-muted">
                      Payment date drives the PAYE tax period. This preview cannot be filed with HMRC or used as the final payroll lock yet.
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
                    <div className="w-full sm:w-56">
                      <Label htmlFor="payroll-payment-date">Payment date</Label>
                      <Input
                        id="payroll-payment-date"
                        type="date"
                        value={paymentDate}
                        onChange={(e) => setPaymentDate(e.target.value)}
                      />
                    </div>
                    <Button type="button" variant="outline" onClick={handleCalculationPreview} disabled={calculationLoading || !paymentDate}>
                      {calculationLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Banknote className="h-4 w-4 mr-2" />}
                      Calculate PAYE, NI & net pay
                    </Button>
                  </div>

                  {calculationPreview?.summary ? (
                    <div className="space-y-3">
                      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4 text-sm">
                        <div className="rounded-lg border bg-white p-3"><div className="text-xs text-text-muted">Gross pay</div><div className="font-semibold">{formatMoney(calculationPreview.summary.gross_pay)}</div></div>
                        <div className="rounded-lg border bg-white p-3"><div className="text-xs text-text-muted">PAYE Income Tax</div><div className="font-semibold">{formatMoney(calculationPreview.summary.paye_income_tax)}</div></div>
                        <div className="rounded-lg border bg-white p-3"><div className="text-xs text-text-muted">Employee NI</div><div className="font-semibold">{formatMoney(calculationPreview.summary.employee_ni)}</div></div>
                        <div className="rounded-lg border bg-white p-3"><div className="text-xs text-text-muted">Employer NI</div><div className="font-semibold">{formatMoney(calculationPreview.summary.employer_ni)}</div></div>
                        <div className="rounded-lg border bg-white p-3"><div className="text-xs text-text-muted">Employee pension</div><div className="font-semibold">{formatMoney(calculationPreview.summary.employee_pension)}</div></div>
                        <div className="rounded-lg border bg-white p-3"><div className="text-xs text-text-muted">Employer pension</div><div className="font-semibold">{formatMoney(calculationPreview.summary.employer_pension)}</div></div>
                        <div className="rounded-lg border bg-white p-3"><div className="text-xs text-text-muted">Student / PG loans</div><div className="font-semibold">{formatMoney(Number(calculationPreview.summary.student_loan || 0) + Number(calculationPreview.summary.postgraduate_loan || 0))}</div></div>
                        <div className="rounded-lg border bg-white p-3"><div className="text-xs text-text-muted">Net pay preview</div><div className="font-semibold">{formatMoney(calculationPreview.summary.net_pay_preview)}</div></div>
                      </div>
                      <div className="grid gap-2 sm:grid-cols-3 text-sm">
                        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                          <div className="text-xs text-blue-700">HMRC liability preview</div>
                          <div className="font-semibold text-blue-900">{formatMoney(calculationPreview.summary.hmrc_liability_preview)}</div>
                        </div>
                        <div className="rounded-lg border border-violet-200 bg-violet-50 p-3">
                          <div className="text-xs text-violet-700">Pension provider liability</div>
                          <div className="font-semibold text-violet-900">{formatMoney(calculationPreview.summary.pension_provider_liability_preview)}</div>
                        </div>
                        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                          <div className="text-xs text-slate-600">Employer total cost preview</div>
                          <div className="font-semibold text-slate-900">{formatMoney(calculationPreview.summary.employer_total_cost_preview)}</div>
                        </div>
                      </div>
                      {Number(calculationPreview.summary.blocked_count || 0) > 0 ? (
                        <div className="rounded-lg border border-amber-300 bg-white p-3">
                          <div className="text-sm font-medium text-amber-900">
                            {calculationPreview.summary.blocked_count} worker(s) blocked from the preview
                          </div>
                          <ul className="mt-1 text-xs text-amber-800 list-disc list-inside space-y-1">
                            {(calculationPreview.blocked_workers || []).map((row, index) => (
                              <li key={`${row.employee_id || 'unknown'}-${index}`}>
                                {row.employee_name || row.employee_id || 'Unknown worker'}: {row.reason}
                                {row.missing_fields?.length ? ` — missing ${row.missing_fields.map((field) => field.label).join(', ')}` : ''}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>

                <div className="flex flex-wrap gap-2 pt-2">
                  {detail.status === 'draft' && (
                    <>
                      <Button size="sm" onClick={handleLock} disabled={actionLoading}>
                        <Lock className="h-4 w-4 mr-1" /> Lock
                      </Button>
                      <Button size="sm" variant="outline" onClick={handleVoid} disabled={actionLoading}>
                        <Ban className="h-4 w-4 mr-1" /> Void
                      </Button>
                    </>
                  )}
                  {(detail.status === 'locked' || detail.status === 'exported') && (
                    <>
                      <Button size="sm" onClick={() => handleExport('csv')} disabled={actionLoading}>
                        <Download className="h-4 w-4 mr-1" /> Export CSV
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleExport('pdf')} disabled={actionLoading}>
                        <FileText className="h-4 w-4 mr-1" /> Export PDF
                      </Button>
                    </>
                  )}
                  {detail.status === 'locked' && (
                    <Button size="sm" variant="outline" onClick={handleVoid} disabled={actionLoading}>
                      <Ban className="h-4 w-4 mr-1" /> Void
                    </Button>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
